<template>
  <v-container class="level-picker">
    <!-- Trigger -->
    <div
      ref="triggerElement"
      class="picker-trigger"
      :class="[selectedLevelClass, { open: isOpen }]"
      @click.stop="toggleOpen"
    >
      <span class="trigger-label">{{ selectedLabel || label }}</span>
      <v-icon class="chevron">{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
    </div>

    <!-- Dropdown -->
    <div v-if="isOpen" class="dropdown" :style="dropdownStyle">
      <v-list class="list">
        <v-list-item
          v-for="lvl in levels"
          :key="`level-${lvl.id}`"
          class="list-item"
          :class="[levelClass(lvl), { 'is-selected': isSelected(lvl), 'not-selectable': isSelected(lvl) }]"
          @click.stop="!isSelected(lvl) && selectLevel(lvl)"
        >
          <v-list-item-content>
            <v-list-item-title class="item-row">
              <span class="checkpoint-item-name">{{ lvl.name }} Level</span>
              <v-icon v-if="isSelected(lvl)" class="check">mdi-check</v-icon>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </div>
  </v-container>
</template>


<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";

const props = defineProps({
  value: { type: Object, default: null },
  label: { type: String, required: true },
  levels: { type: Array, required: true },
  disabled: { type: Boolean, default: false },
});

// Emits
const emit = defineEmits(["input", "item-selected"]);

const isOpen = ref(false);
const dropdownStyle = ref({});

// Global dropdown management
const DROPDOWN_EVENT = 'checkpoint-level-picker-toggle';
const DROPDOWN_CLOSE_EVENT = 'checkpoint-level-picker-close';

const selectedLevel = computed(() =>
  props.value || (Array.isArray(props.levels) && props.levels[0]) || null
);

const selectedLabel = computed(() =>
  selectedLevel.value ? `${selectedLevel.value.name} Level` : ""
);

const selectedLevelClass = computed(() =>
  selectedLevel.value ? levelTone(selectedLevel.value) : "tone-neutral"
);

// Methods
function toggleOpen() {
  if (props.disabled) return;
  
  if (!isOpen.value) {
    // Close all other dropdowns first (use standard Event for jsdom compatibility)
    window.dispatchEvent(new Event(DROPDOWN_CLOSE_EVENT));
    // Open this dropdown
    isOpen.value = true;
    calculateDropdownPosition();
    document.addEventListener("click", closeOnOutside, { once: true });
  } else {
    // Close this dropdown
    isOpen.value = false;
  }
}

function calculateDropdownPosition() {
  if (!triggerElement.value) return;
  
  const triggerRect = triggerElement.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  
  // Calculate approximate dropdown height based on number of items
  const itemHeight = 48; // Approximate height per item (padding + text)
  const dropdownHeight = Math.min(props.levels.length * itemHeight + 16, 200); // Max 200px, add padding
  
  const spaceBelow = viewportHeight - triggerRect.bottom;
  const spaceAbove = triggerRect.top;
  
  // Check if there's enough space below, if not, position above
  const shouldOpenAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
  
  dropdownStyle.value = {
    zIndex: 10,
    top: shouldOpenAbove ? 'auto' : '100%',
    bottom: shouldOpenAbove ? '90%' : 'auto',
    marginTop: shouldOpenAbove ? '0' : '3px',
    marginBottom: shouldOpenAbove ? '3px' : '0'
  };
}

function closeOnOutside(e) {
  if (!triggerElement.value?.closest('.level-picker')?.contains(e.target)) {
    isOpen.value = false;
  }
}

function selectLevel(item) {
  isOpen.value = false;
  emit("input", item);
  emit("item-selected", item);
}

function isSelected(item) {
  return selectedLevel.value && String(selectedLevel.value.id) === String(item.id);
}

function levelTone(item) {
  const key = (item.name || "").toLowerCase();
  if (key.includes("apprentice")) return "tone-apprentice";
  if (key.includes("proficient") || key.includes("professional")) return "tone-proficient";
  if (key.includes("coach")) return "tone-coach";
  if (key.includes("architect") || key.includes("master")) return "tone-architect";
  return "tone-neutral";
}

function levelClass(item) {
  return ["item", levelTone(item)];
}

// Template refs
const triggerElement = ref(null);

// Event handlers
function handleGlobalClose() {
  isOpen.value = false;
}

function handleResize() {
  if (isOpen.value) {
    calculateDropdownPosition();
  }
}

// On mounted
onMounted(() => {
  if (!props.value && Array.isArray(props.levels) && props.levels.length) {
    emit("input", props.levels[0]);
  }
  
  window.addEventListener('resize', handleResize);
  window.addEventListener(DROPDOWN_CLOSE_EVENT, handleGlobalClose);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener(DROPDOWN_CLOSE_EVENT, handleGlobalClose);
});
</script>

<style scoped>
.level-picker {
  position: relative;
  width: 290px; /* Adjust width of dropdown */
}

/* Trigger button */
.picker-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}
.picker-trigger.open {
  box-shadow: 0 0 0 3px #e9d7ff;
  border-color: #d6bbfb;
}
.chevron {
  color: #667085;
}
.trigger-label {
  font-weight: 600;
}

/* Tone colors for trigger */
.tone-apprentice {
  background: #fff5d3;
  color: #e58700;
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
}
.tone-proficient {
  background: #f3fff9;
  color: #00b813;
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
}
.tone-coach {
  background: #effeff;
  color: #00a3ff;
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
}
.tone-architect {
  background: #f2f3ff;
  color: #2e1ec1;
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
}
.tone-neutral {
  background: #ffffff;
  color: #101828;
}

/* Dropdown */
.dropdown {
  position: absolute;
  width: 270px; /* same width as trigger */
  top: auto !important;
  left: 10px;
  z-index: 10;
}
.list {
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 4px 6px -2px #10182808, 0px 12px 16px -4px #10182814 !important;
}
.list-item {
  padding: 0 14px;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
}
.list-item .v-list-item__content {
  padding: 0px;
  width: 100%;
  box-sizing: border-box;
}
.item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  font-weight: 600;
  width: 100%;
  box-sizing: border-box;
}
.item {
  background: #fff;
  transition: background 0.2s;
}

/* Tone text colors */
.item.tone-apprentice {
  color: #c2410c;
}
.item.tone-proficient {
  color: #039855;
}
.item.tone-coach {
  color: #175cd3;
}
.item.tone-architect {
  color: #7a5af8;
}

/* Hover states */
.item.tone-apprentice:hover {
  background: #fff5d3;
  width: 260px;
  margin: 0 auto;
}
.item.tone-proficient:hover {
  background: #f3fff9;
  width: 260px;
  margin: 0 auto;
}
.item.tone-coach:hover {
  background: #effeff;
  width: 260px;
  margin: 0 auto;
}
.item.tone-architect:hover {
  background: #f2f3ff;
  width: 260px;
  margin: 0 auto;
}

/* Selected row highlights */
.item.is-selected.tone-apprentice {
  background: #fff5d3;
}
.item.is-selected.tone-proficient {
  background: #f3fff9;
}
.item.is-selected.tone-coach {
  background: #effeff;
}
.item.is-selected.tone-architect {
  background: #f2f3ff;
}

.tone-apprentice .checkpoint-item-name {
  color: #e58700;
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
}
.tone-proficient .checkpoint-item-name {
  color: #00b813;
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
}
.tone-coach .checkpoint-item-name {
  color: #00a3ff;
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
}
.tone-architect .checkpoint-item-name {
  color: #2e1ec1;
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
}

.list-item.v-list-item:hover::before {
  opacity: 0;
}

.list-item.v-list-item {
  width: 260px;
  margin: 0 auto;
}

/* Check icon */
.check {
  color: #7a5af8;
  position: relative;
  left: 4px;
}
.list-item.not-selectable {
  cursor: not-allowed;
  opacity: 0.8;
}
</style>
